import React from 'react';

import { IconCash } from '@lightspeed/flame/Icon/Cash';

import { createReactRenderer } from 'helpers/react-renderer';
import { renderPaymentRequestModal } from 'root/apps/QuoteMachineIntegration/components/PaymentRequest/Modal/Modal';
import TooltipHelp from 'root/apps/Tooltip/components/TooltipHelp';

type Props = {
  disabled: boolean;
  hasRefundLines?: boolean;
};

const tooltipContent = 'Attach a customer to the sale to enable payment requests.';

const CLASSNAMES = {
  workbench: 'gui-def-button payment-request-button--workbench',
  register: 'cr-button cr-button--large flex',
} as const;

const WORKBENCH_BUTTON_STYLE: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  padding: '0px 12px',
  border: '1px solid rgb(42 90 177)',
  borderRadius: '3px',
  color: 'white',
  backgroundColor: 'rgb(38 75 115)',
  cursor: 'pointer',
};

const REGISTER_BUTTON_STYLE: React.CSSProperties = {
  gap: '8px',
  padding: 0,
};

const WorkbenchButtonContent: React.FC = () => <>Request payment</>;

const RegisterButtonContent: React.FC = () => (
  <>
    <span className="flex">
      <IconCash />
    </span>
    <span className="cr-button__content flex">Request payment</span>
  </>
);

const PaymentRequestButton: React.FC<Props> = ({ disabled, hasRefundLines }) => {
  if (hasRefundLines) {
    return null;
  }

  // Detect if we're in workbench context by checking the URL
  const isWorkbenchContext = window.location.search.includes('workbench');
  const className = isWorkbenchContext ? CLASSNAMES.workbench : CLASSNAMES.register;
  const style = isWorkbenchContext ? WORKBENCH_BUTTON_STYLE : REGISTER_BUTTON_STYLE;
  const buttonContent = isWorkbenchContext ? <WorkbenchButtonContent /> : <RegisterButtonContent />;

  function isDisabled(): boolean {
    return disabled || (isWorkbenchContext && !window.merchantos.session.getRegister()?.registerID);
  }

  function getTooltipContent(): string {
    if (isWorkbenchContext && !window.merchantos.session.getRegister()?.registerID) {
      return 'Please open a register to create a payment request.';
    }
    return tooltipContent;
  }

  const content = (
    <button
      type="button"
      id="requestDepositButton"
      className={className}
      style={style}
      disabled={isDisabled()}
      onClick={() => {
        if (!isDisabled()) {
          if (isWorkbenchContext) {
            // Handle workbench context - get workorder ID from URL
            const urlParams = new URLSearchParams(window.location.search);
            const workorderId = urlParams.get('id');

            if (workorderId) {
              renderPaymentRequestModal({
                workorderID: workorderId,
                onSubmit: () => {},
                handleCancel: () => {},
                saleID: '',
              });
            }
          } else {
            // Handle register context
            window.merchantos.register.previewPaymentRequest();
          }
        }
      }}
    >
      {buttonContent}
    </button>
  );

  return isDisabled() ? (
    <TooltipHelp
      content={getTooltipContent()}
      placement="bottom"
      icon={content}
      width="216px"
      data-testid="payment-request-button-tool-tip"
    />
  ) : (
    content
  );
};

export default PaymentRequestButton;

const RenderedComponent = createReactRenderer({
  component: PaymentRequestButton,
  selector: '[data-react-root="PaymentRequestButton"]',
});

history.onChangeState(
  [
    { pathname: '/register.php', form_name: undefined, view: undefined },
    { name: ['workbench.views.beta_workorder'], form_name: 'view', tab: 'details', id: undefined },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
